// NewsletterSignUp.js
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { getCurrentReader } from "../../auth/getCurrentReader";
import ReaderPageLayout from "../layouts/ReaderPageLayout";

export default function NewsletterSignUp() {
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [email, setEmail] = useState("");

  const [newsletterTitle, setNewsletterTitle] = useState("");
  const [newsletterTagline, setNewsletterTagline] = useState("");
  const [loading, setLoading] = useState(true);

  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);

  const SUBSCRIBE_API =
    "https://h22t7gm28c.execute-api.us-west-2.amazonaws.com/default/add-subscriber";

  const NEWSLETTER_GET_API =
    "https://0y5pird9if.execute-api.us-west-2.amazonaws.com/default/get-NLsignup-reader";

  useEffect(() => {
    const loadNewsletter = async () => {
      try {
        const host = window.location.hostname;
        const res = await fetch(
          `${NEWSLETTER_GET_API}?host=${encodeURIComponent(host)}`
        );
        const data = await res.json();

        if (data?.newsletterTitle) {
          setNewsletterTitle(data.newsletterTitle);
        }

        if (data?.newsletterTagline) {
          setNewsletterTagline(data.newsletterTagline);
        }
      } catch (err) {
        console.error("Newsletter GET error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadNewsletter();
  }, []);

  useEffect(() => {
    let active = true;
    getCurrentReader().then((reader) => {
      if (!active || !reader) return;
      setEmail((value) => value || reader.email || "");
      setFirstname((value) => value || reader.firstName || "");
      setLastname((value) => value || reader.lastName || "");
    });
    return () => { active = false; };
  }, []);

  const handleSignUp = async (e) => {
    e.preventDefault();
    if (submitLock.current) return;
    submitLock.current = true; setSubmitting(true);
    setMessage("");
    setError("");

    try {
      const host = window.location.hostname;

      const response = await axios.post(
        `${SUBSCRIBE_API}?host=${encodeURIComponent(host)}`,
        { email, firstname, lastname },
        { headers: { "Content-Type": "application/json" }, timeout: 20000 }
      );

      setMessage(response.data.message || "Check your email to confirm your subscription.");
    } catch (err) {
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "There was an error submitting your information."
      );
    } finally { submitLock.current = false; setSubmitting(false); }
  };

  return (
    <ReaderPageLayout
      title={loading ? "" : newsletterTitle}
      description={loading ? "" : newsletterTagline}
      centeredHeader
      contentClassName="space-y-6"
    >
      {loading ? (
        <div className="bg-white rounded-2xl shadow-md p-6 sm:p-8 space-y-4 animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/3" />
          <div className="h-10 bg-gray-200 rounded" />
          <div className="h-4 bg-gray-200 rounded w-1/3" />
          <div className="h-10 bg-gray-200 rounded" />
          <div className="h-10 bg-gray-200 rounded" />
        </div>
      ) : (
        <div className="rounded-2xl shadow-md bg-white p-6 sm:p-8">
          <form onSubmit={handleSignUp} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="newsletter-first-name" className="block text-sm text-gray-700 mb-1">First Name</label><input id="newsletter-first-name"
                  type="text"
                  required
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  className="w-full px-4 py-2 rounded-xl border border-bookish-purple/20 bg-white focus:ring-2 focus:ring-bookish-purpleAccent outline-none"
                  placeholder="Your first name"
                />
              </div>

              <div>
                <label htmlFor="newsletter-last-name" className="block text-sm text-gray-700 mb-1">Last Name</label><input id="newsletter-last-name"
                  type="text"
                  required
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  className="w-full px-4 py-2 rounded-xl border border-bookish-purple/20 bg-white focus:ring-2 focus:ring-bookish-purpleAccent outline-none"
                  placeholder="Your last name"
                />
              </div>
            </div>

            <div>
              <label htmlFor="newsletter-email-address" className="block text-sm text-gray-700 mb-1">Email Address</label><input id="newsletter-email-address"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2 rounded-xl border border-bookish-purple/20 bg-white focus:ring-2 focus:ring-bookish-purpleAccent outline-none"
                placeholder="your@email.com"
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-bookish-purple text-white rounded-xl px-4 py-2 font-semibold hover:bg-bookish-purple/90 transition"
            >
              {submitting ? 'Sending confirmation…' : 'Subscribe'}
            </button>

            <p className="text-xs text-gray-700 text-center leading-relaxed">
              Confirm the link in your email to start receiving this author’s newsletter. You can
              unsubscribe at any time via the link in each email.
            </p>
          </form>

          {message && (
            <p role="status" className="mt-6 text-center text-green-700 font-medium">{message}</p>
          )}

          {error && (
            <p role="alert" className="mt-6 text-center text-red-600 font-medium">{error}</p>
          )}
        </div>
      )}
    </ReaderPageLayout>
  );
}
